import React, { useMemo, memo } from 'react';
import fieldActions from 'core/fields/actions';
import dataActions from 'core/data/actions';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

const getDefaultValue = (type) => {
  switch (type) {
    case 'MULTI_DISCLOSURES':
      return false;
    default:
      return '';
  }
};

const getInputmode = (mode, type) => {
  if (type === 'LOCAL_DATE') {
    return 'tel';
  }

  switch (mode) {
    case 'email':
      return 'email';
    case 'number':
      return 'numeric';
    case 'tel':
      return 'tel';
    case 'text':
    default:
      return 'text';
  }
};

const useFieldProps = (
  { groupName, listGroupName, name },
  mapStateToProps = () => ({}),
  mapDispatchToProps = () => ({}),
) => {
  const dispatch = useDispatch();
  const fieldProps = useSelector((state) => {
    const { data, fields, session, ui } = state;
    const [listGroupFieldName, listIndex] = name.split(':');
    const field = fields[name];
    const fieldLoading = session.meta.configs.field_spinny ? ui.fieldLoading : null;
    return {
      copyPasteError:
        session.meta?.error?.fieldDisallowPaste ||
        'Copy-paste and right-click are disabled on this field. Please type the information in to avoid any errors.',
      disallowPaste: field.disallowPaste ?? false,
      error: field.error ?? false,
      errorMsg: field.errorMsg ?? '',
      fieldDescr: field.fieldDescr ?? '',
      fieldDomId: `${groupName}-${name}`,
      format: field.format ?? '',
      hasReRender: !!field.reRender,
      help: field.help ?? '',
      id: field.id,
      inputmode: getInputmode(field.mobileInputType, field.fieldType),
      label: field.label ?? '',
      loading: fieldLoading === name,
      placeholder: field.placeholder ?? '',
      readOnly: field.readOnly ?? false,
      required:
        (groupName === 'LOOKUP_COMPOSITE' && field.conditional) || groupName !== 'LOOKUP_COMPOSITE'
          ? field.required
          : true,
      tooltip: field.tooltip ?? '',
      type: 'text',
      value:
        (listGroupName
          ? data[listGroupName][parseInt(listIndex, 10)][listGroupFieldName]
          : data[name]) ?? getDefaultValue(field.fieldType),
      ...mapStateToProps(state, { groupName, listGroupName, name }),
    };
  }, shallowEqual);
  return useMemo(
    () => ({
      ...fieldProps,
      onBlur: () => {
        dispatch(fieldActions.blur(name));
      },
      onChange: (key, newValue) => {
        if (listGroupName) {
          const [fieldKey, listIndex] = key.split(':');
          dispatch(
            dataActions.changeListGroupData(
              listGroupName,
              parseInt(listIndex, 10),
              fieldKey,
              newValue,
            ),
          );
        } else {
          dispatch(dataActions.changeData(key, newValue));
        }
      },
      onDisable: (e) => {
        e.preventDefault();
        return false;
      },
      onFocus: () => {
        dispatch(fieldActions.focus(name));
      },
      onPaste: (e) => {
        if (fieldProps.disallowPaste) {
          e.preventDefault();
          dispatch(fieldActions.setError(name, fieldProps.copyPasteError));
        }
      },
      validate: (withReRender) => {
        const [fieldKey, listIndex] = name.split(':');
        dispatch(
          fieldActions.validate(fieldKey, withReRender, listGroupName, parseInt(listIndex, 10)),
        );
      },
      ...mapDispatchToProps(dispatch, { ...fieldProps, groupName, listGroupName, name }),
    }),
    [dispatch, fieldProps, groupName, listGroupName, mapDispatchToProps, name],
  );
};

export const withFieldProps = (mapStateToProps, mapDispatchToProps) => (WrappedComponent) =>
  memo((props) => {
    const fieldProps = useFieldProps(props, mapStateToProps, mapDispatchToProps);
    return <WrappedComponent {...props} {...fieldProps} />;
  });

export default useFieldProps;
